import { useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { communityApi, isApiError, programApi } from "@bw/shared/api";
import {
  useClassAssignments,
  useClassAssignmentTargets,
  useClassLearningSpace,
  useClassLearningLearners,
  useClassMaterials,
  useClassRetention,
  useClassSubmissions,
  useConfigureClassRetention,
  usePublishClassAssignment,
  useRemoveClassFile,
  useReviewClassSubmission,
  useSaveClassAssignment,
  useSaveClassMaterial,
  useTransitionClassMaterial,
  useUploadClassFile,
  useAuth,
} from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";
import { CommunityManageView } from "@/components/community/community-manage-view";

type Material = Schema["ClassMaterial"];
type Assignment = Schema["ClassAssignment"];

function command() {
  return crypto.randomUUID();
}

function statusLabel(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toLocalInput(value: string | null) {
  if (!value) return "";
  const instant = new Date(value);
  const local = new Date(
    instant.getTime() - instant.getTimezoneOffset() * 60_000,
  );
  return local.toISOString().slice(0, 16);
}

function fromLocalInput(value: string) {
  return value ? new Date(value).toISOString() : null;
}

async function download(fileId: string, enrollmentId?: string) {
  const result = await programApi.downloadClassFile(fileId, enrollmentId);
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function academyError(error: unknown): string {
  if (isApiError(error) && error.status === 409)
    return "This record changed. Refresh it before trying again.";
  if (isApiError(error) && error.status === 403)
    return "Your Academy access changed. Ask an owner or manager to review your role.";
  if (isApiError(error) && error.status === 503)
    return "Academy is temporarily unavailable. Your existing class remains unchanged.";
  return "We couldn’t complete that Academy action. Please try again.";
}

function isAccessError(error: unknown): boolean {
  return isApiError(error) && [401, 403, 404].includes(error.status);
}

export function ProgramAcademyWorkspace({ programId }: { programId: string }) {
  const [tab, setTab] = useState<"materials" | "assignments" | "discussion">(
    "materials",
  );
  const [materialOffset, setMaterialOffset] = useState(0);
  const [assignmentOffset, setAssignmentOffset] = useState(0);
  const retention = useClassRetention(programId);
  const materials = useClassMaterials(programId, undefined, {
    offset: materialOffset,
  });
  const assignments = useClassAssignments(programId, undefined, {
    offset: assignmentOffset,
  });
  const configure = useConfigureClassRetention(programId);
  const [retentionDays, setRetentionDays] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  if (
    [retention, materials, assignments].some(
      (query) => query.isError && isAccessError(query.error),
    )
  )
    return (
      <Alert severity="warning">
        Your Academy access changed. Return to Programs to refresh your current
        workspace.
      </Alert>
    );

  async function saveRetention() {
    const days = Number(retentionDays);
    if (!Number.isInteger(days) || days < 30 || days > 3650 || !retention.data)
      return;
    setNotice(null);
    try {
      await configure.mutateAsync({
        retention_days: days,
        expected_revision: retention.data.revision,
        idempotency_key: command(),
      });
      setRetentionDays("");
    } catch (error) {
      setNotice(academyError(error));
    }
  }

  const busy =
    retention.isLoading || materials.isLoading || assignments.isLoading;
  return (
    <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
        >
          <Box>
            <Typography component="h2" variant="h5">
              Academy
            </Typography>
            <Typography color="text.secondary">
              Course resources, assignments and private learner work in one
              place.
            </Typography>
          </Box>
          {retention.data?.configured ? (
            <Chip
              color="success"
              variant="outlined"
              label={`${retention.data.retention_days} day fixed retention`}
            />
          ) : (
            <Chip color="warning" label="Retention setup required" />
          )}
        </Stack>
      </Box>
      {busy ? <LinearProgress /> : null}
      <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
        {notice ? (
          <Alert severity="warning" onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        ) : null}
        {retention.isError ? (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => void retention.refetch()}>
                Retry
              </Button>
            }
          >
            Academy settings could not be loaded.
          </Alert>
        ) : null}
        {retention.data && !retention.data.configured ? (
          <Card variant="outlined" sx={{ borderColor: "warning.main" }}>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">
                    Choose a fixed history period
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Learner work stays readable for this period after the final
                    class session. Activity does not restart the clock, and
                    existing promises are never shortened.
                  </Typography>
                </Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField
                    label="Retention days"
                    type="number"
                    value={retentionDays}
                    onChange={(event) => setRetentionDays(event.target.value)}
                    slotProps={{ htmlInput: { min: 30, max: 3650 } }}
                  />
                  <Button
                    variant="contained"
                    disabled={configure.isPending || !retentionDays}
                    onClick={() => void saveRetention()}
                  >
                    Save policy
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ) : null}
        <Tabs
          value={tab}
          onChange={(
            _event,
            value: "materials" | "assignments" | "discussion",
          ) => setTab(value)}
          variant="fullWidth"
          aria-label="Academy workspace"
        >
          <Tab
            value="materials"
            label={`Materials${materials.data ? ` (${materials.data.total})` : ""}`}
          />
          <Tab
            value="assignments"
            label={`Assignments${assignments.data ? ` (${assignments.data.total})` : ""}`}
          />
          <Tab value="discussion" label="Discussion" />
        </Tabs>
        {tab === "materials" ? (
          materials.isError ? (
            <AcademyLoadError
              label="Course materials"
              retry={() => void materials.refetch()}
            />
          ) : (
            <MaterialsWorkspace
              programId={programId}
              page={materials.data}
              disabled={!retention.data?.configured}
              onError={setNotice}
              onPage={setMaterialOffset}
            />
          )
        ) : null}
        {tab === "assignments" ? (
          assignments.isError ? (
            <AcademyLoadError
              label="Assignments"
              retry={() => void assignments.refetch()}
            />
          ) : (
            <AssignmentsWorkspace
              programId={programId}
              page={assignments.data}
              disabled={!retention.data?.configured}
              onError={setNotice}
              onPage={setAssignmentOffset}
            />
          )
        ) : null}
        {tab === "discussion" ? (
          <ClassDiscussionWorkspace programId={programId} />
        ) : null}
      </Stack>
    </Paper>
  );
}

function ClassDiscussionWorkspace({ programId }: { programId: string }) {
  const { user } = useAuth();
  const space = useClassLearningSpace(programId);
  const community = useQuery({
    queryKey: [
      "academy",
      user?.uid ?? null,
      "discussion",
      space.data?.community_id ?? null,
    ],
    queryFn: () => communityApi.get(space.data!.community_id),
    enabled: Boolean(user?.uid && space.data?.community_id),
    retry: false,
    refetchOnMount: "always",
  });
  if (space.isLoading || community.isLoading)
    return <Typography role="status">Loading class discussion…</Typography>;
  if (space.isError || community.isError)
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            onClick={() => {
              void space.refetch();
              void community.refetch();
            }}
          >
            Retry
          </Button>
        }
      >
        Class discussion could not be loaded.
      </Alert>
    );
  if (!community.data) return null;
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Class discussion</Typography>
        <Typography variant="body2" color="text.secondary">
          Post announcements and follow the class discussion here.
        </Typography>
      </Box>
      <CommunityManageView
        key={community.data.community_id}
        community={community.data}
        facilityId={community.data.owner_facility_id ?? ""}
        canManage={false}
        canPost={community.data.can_post}
        canModerate={false}
      />
    </Stack>
  );
}

function MaterialsWorkspace({
  programId,
  page,
  disabled,
  onError,
  onPage,
}: {
  programId: string;
  page: Schema["ClassMaterialPage"] | undefined;
  disabled: boolean;
  onError: (message: string) => void;
  onPage: (offset: number) => void;
}) {
  const save = useSaveClassMaterial(programId);
  const transition = useTransitionClassMaterial(programId);
  const upload = useUploadClassFile(programId);
  const remove = useRemoveClassFile(programId);
  const [editing, setEditing] = useState<Material | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  function open(value: Material | "new") {
    setEditing(value);
    setTitle(value === "new" ? "" : value.title);
    setBody(value === "new" ? "" : (value.body ?? ""));
  }

  async function persist() {
    if (!editing || !title.trim()) return;
    try {
      await save.mutateAsync({
        materialId: editing === "new" ? undefined : editing.material_id,
        body: {
          scope: editing === "new" ? { kind: "class" } : editing.scope,
          title,
          body: body || null,
          position: editing === "new" ? (page?.total ?? 0) : editing.position,
          expected_revision: editing === "new" ? 0 : editing.revision,
          idempotency_key: command(),
          file_ids: [],
        },
      });
      setEditing(null);
    } catch (error) {
      onError(academyError(error));
    }
  }

  async function addFile(
    material: Material,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const stored = await upload.mutateAsync({
        file,
        reserve: {
          subject_kind: "material",
          subject_id: material.material_id,
          enrollment_id: null,
          expected_subject_revision: material.revision,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          idempotency_key: command(),
        },
      });
      await save.mutateAsync({
        materialId: material.material_id,
        body: {
          scope: material.scope,
          title: material.title,
          body: material.body,
          position: material.position,
          expected_revision: material.revision,
          idempotency_key: command(),
          file_ids: [stored.file_id],
        },
      });
    } catch (error) {
      onError(academyError(error));
    }
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
      >
        <Box>
          <Typography variant="h6">Course library</Typography>
          <Typography variant="body2" color="text.secondary">
            Publish reusable notes and protected files without exposing public
            URLs.
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={() => open("new")}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        >
          New material
        </Button>
      </Stack>
      {!page ? (
        <Typography role="status">Loading materials…</Typography>
      ) : page.items.length === 0 ? (
        <EmptyState
          title="No materials yet"
          detail="Start with a practice plan, class note or protected handout."
        />
      ) : (
        page.items.map((material) => (
          <Card key={material.material_id} variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Box>
                    <Typography variant="h6">{material.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {material.scope.kind === "class"
                        ? "Whole class"
                        : material.scope.kind === "track"
                          ? "Track"
                          : "Session"}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    variant={
                      material.state === "published" ? "filled" : "outlined"
                    }
                    color={
                      material.state === "published" ? "success" : "default"
                    }
                    label={statusLabel(material.state)}
                  />
                </Stack>
                {material.body ? (
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {material.body}
                  </Typography>
                ) : null}
                {material.attachments.length ? (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {material.attachments.map((file) => (
                      <Chip
                        key={file.file_id}
                        size="small"
                        label={file.filename}
                        clickable
                        onClick={() =>
                          void download(file.file_id).catch((error) =>
                            onError(academyError(error)),
                          )
                        }
                        onDelete={
                          file.can_remove
                            ? () =>
                                void remove
                                  .mutateAsync({
                                    fileId: file.file_id,
                                    expectedRevision: file.revision,
                                  })
                                  .catch((error) =>
                                    onError(academyError(error)),
                                  )
                            : undefined
                        }
                      />
                    ))}
                  </Stack>
                ) : null}
                <Divider />
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  {material.can_edit ? (
                    <Button size="small" onClick={() => open(material)}>
                      Edit
                    </Button>
                  ) : null}
                  {material.can_edit ? (
                    <Button
                      component="label"
                      size="small"
                      disabled={upload.isPending}
                    >
                      Add file
                      <input
                        hidden
                        type="file"
                        onChange={(event) => void addFile(material, event)}
                      />
                    </Button>
                  ) : null}
                  {material.can_publish ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        void transition
                          .mutateAsync({
                            materialId: material.material_id,
                            transition: "publish",
                            body: {
                              expected_revision: material.revision,
                              idempotency_key: command(),
                            },
                          })
                          .catch((error) => onError(academyError(error)))
                      }
                    >
                      Publish
                    </Button>
                  ) : null}
                  {material.can_archive ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() =>
                        void transition
                          .mutateAsync({
                            materialId: material.material_id,
                            transition: "archive",
                            body: {
                              expected_revision: material.revision,
                              idempotency_key: command(),
                            },
                          })
                          .catch((error) => onError(academyError(error)))
                      }
                    >
                      Archive
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))
      )}
      {page ? <AcademyPager page={page} onPage={onPage} /> : null}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing === "new" ? "New course material" : "Edit material"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <TextField
              label="Notes or instructions"
              multiline
              minRows={5}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              slotProps={{ htmlInput: { maxLength: 10000 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={save.isPending || !title.trim()}
            onClick={() => void persist()}
          >
            Save draft
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function AssignmentsWorkspace({
  programId,
  page,
  disabled,
  onError,
  onPage,
}: {
  programId: string;
  page: Schema["ClassAssignmentPage"] | undefined;
  disabled: boolean;
  onError: (message: string) => void;
  onPage: (offset: number) => void;
}) {
  const save = useSaveClassAssignment(programId);
  const publish = usePublishClassAssignment(programId);
  const [learnerOffset, setLearnerOffset] = useState(0);
  const learners = useClassLearningLearners(programId, {
    offset: learnerOffset,
  });
  const upload = useUploadClassFile(programId);
  const remove = useRemoveClassFile(programId);
  const [editing, setEditing] = useState<Assignment | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [publishing, setPublishing] = useState<Assignment | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reviewing, setReviewing] = useState<Assignment | null>(null);

  function open(value: Assignment | "new") {
    setEditing(value);
    setTitle(value === "new" ? "" : value.title);
    setInstructions(value === "new" ? "" : (value.instructions ?? ""));
    setDueAt(value === "new" ? "" : toLocalInput(value.due_at));
    setClosesAt(value === "new" ? "" : toLocalInput(value.closes_at));
  }

  async function persist() {
    if (!editing) return;
    try {
      await save.mutateAsync({
        assignmentId: editing === "new" ? undefined : editing.assignment_id,
        body: {
          scope: editing === "new" ? { kind: "class" } : editing.scope,
          title,
          instructions,
          due_at: fromLocalInput(dueAt),
          closes_at: fromLocalInput(closesAt),
          expected_revision: editing === "new" ? 0 : editing.current_revision,
          idempotency_key: command(),
          file_ids: [],
        },
      });
      setEditing(null);
      setTitle("");
      setInstructions("");
      setDueAt("");
      setClosesAt("");
    } catch (error) {
      onError(academyError(error));
    }
  }

  async function addFile(
    assignment: Assignment,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const stored = await upload.mutateAsync({
        file,
        reserve: {
          subject_kind: "assignment_revision",
          subject_id: assignment.assignment_id,
          enrollment_id: null,
          expected_subject_revision: assignment.current_revision,
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          idempotency_key: command(),
        },
      });
      await save.mutateAsync({
        assignmentId: assignment.assignment_id,
        body: {
          scope: assignment.scope,
          title: assignment.title,
          instructions: assignment.instructions ?? "",
          due_at: assignment.due_at,
          closes_at: assignment.closes_at,
          expected_revision: assignment.current_revision,
          idempotency_key: command(),
          file_ids: [stored.file_id],
        },
      });
    } catch (error) {
      onError(academyError(error));
    }
  }

  async function publishSelected() {
    if (!publishing || selected.length === 0) return;
    try {
      await publish.mutateAsync({
        assignmentId: publishing.assignment_id,
        body: {
          enrollment_ids: selected,
          expected_revision: publishing.current_revision,
          idempotency_key: command(),
        },
      });
      setPublishing(null);
      setSelected([]);
    } catch (error) {
      onError(academyError(error));
    }
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}
      >
        <Box>
          <Typography variant="h6">Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            Target exact course enrollments and review each learner privately.
          </Typography>
        </Box>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={() => open("new")}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        >
          New assignment
        </Button>
      </Stack>
      {!page ? (
        <Typography role="status">Loading assignments…</Typography>
      ) : page.items.length === 0 ? (
        <EmptyState
          title="No assignments yet"
          detail="Create a reflection, practice task or take-home drill."
        />
      ) : (
        page.items.map((assignment) => (
          <Card key={assignment.assignment_id} variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ justifyContent: "space-between" }}
                >
                  <Box>
                    <Typography variant="h6">{assignment.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {assignment.instructions}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={statusLabel(assignment.state)}
                    color={
                      assignment.state === "published" ? "success" : "default"
                    }
                  />
                </Stack>
                <Typography variant="body2">
                  {assignment.target_count} assigned ·{" "}
                  {assignment.submitted_count} submitted
                </Typography>
                {assignment.due_at || assignment.closes_at ? (
                  <Typography variant="body2" color="text.secondary">
                    {assignment.due_at
                      ? `Due ${new Date(assignment.due_at).toLocaleString()}`
                      : "No due date"}
                    {assignment.closes_at
                      ? ` · Closes ${new Date(assignment.closes_at).toLocaleString()}`
                      : ""}
                  </Typography>
                ) : null}
                {assignment.attachments.length ? (
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    {assignment.attachments.map((file) => (
                      <Chip
                        key={file.file_id}
                        size="small"
                        label={file.filename}
                        clickable
                        onClick={() =>
                          void download(file.file_id).catch((error) =>
                            onError(academyError(error)),
                          )
                        }
                        onDelete={
                          file.can_remove
                            ? () =>
                                void remove
                                  .mutateAsync({
                                    fileId: file.file_id,
                                    expectedRevision: file.revision,
                                  })
                                  .catch((error) =>
                                    onError(academyError(error)),
                                  )
                            : undefined
                        }
                      />
                    ))}
                  </Stack>
                ) : null}
                <Divider />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {assignment.can_edit ? (
                    <Button size="small" onClick={() => open(assignment)}>
                      Edit
                    </Button>
                  ) : null}
                  {assignment.can_edit ? (
                    <Button component="label" size="small">
                      Add file
                      <input
                        hidden
                        type="file"
                        onChange={(event) => void addFile(assignment, event)}
                      />
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    disabled={!assignment.can_publish}
                    onClick={() => {
                      setPublishing(assignment);
                      setSelected([]);
                    }}
                  >
                    Assign learners
                  </Button>
                  <Button
                    size="small"
                    disabled={assignment.target_count === 0}
                    onClick={() => setReviewing(assignment)}
                  >
                    Review work
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))
      )}
      {page ? <AcademyPager page={page} onPage={onPage} /> : null}
      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing === "new" ? "New assignment" : "Edit assignment"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
            <TextField
              label="Instructions"
              multiline
              minRows={5}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Due date"
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                label="Submission closes"
                type="datetime-local"
                value={closesAt}
                onChange={(event) => setClosesAt(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!title.trim() || !instructions.trim() || save.isPending}
            onClick={() => void persist()}
          >
            Save draft
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={publishing !== null}
        onClose={() => setPublishing(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Choose learners</DialogTitle>
        <DialogContent>
          <Stack sx={{ pt: 1 }}>
            {learners.data?.items.map((learner) => (
              <FormControlLabel
                key={learner.enrollment_id}
                control={
                  <Checkbox
                    checked={selected.includes(learner.enrollment_id)}
                    onChange={(_event, checked) =>
                      setSelected((value) =>
                        checked
                          ? [...value, learner.enrollment_id]
                          : value.filter((id) => id !== learner.enrollment_id),
                      )
                    }
                  />
                }
                label={`${learner.learner_display_name || "Learner"} · ${learner.track_label}`}
              />
            )) ?? <Typography>Loading learners…</Typography>}
            {learners.isError ? (
              <AcademyLoadError
                label="Learners"
                retry={() => void learners.refetch()}
              />
            ) : null}
            {learners.data ? (
              <AcademyPager page={learners.data} onPage={setLearnerOffset} />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishing(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={selected.length === 0 || publish.isPending}
            onClick={() => void publishSelected()}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
      {reviewing ? (
        <AssignmentReviewDialog
          programId={programId}
          assignment={reviewing}
          onClose={() => setReviewing(null)}
          onError={onError}
        />
      ) : null}
    </Stack>
  );
}

function AssignmentReviewDialog({
  programId,
  assignment,
  onClose,
  onError,
}: {
  programId: string;
  assignment: Assignment;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [targetOffset, setTargetOffset] = useState(0);
  const targets = useClassAssignmentTargets(
    programId,
    assignment.assignment_id,
    { offset: targetOffset },
  );
  const [selected, setSelected] = useState<
    Schema["ClassAssignmentTarget"] | null
  >(null);
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogTitle>Review · {assignment.title}</DialogTitle>
      <DialogContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ pt: 1 }}
        >
          <Stack spacing={1} sx={{ minWidth: { md: 260 } }}>
            {targets.data?.items.map((target) => (
              <Button
                key={target.target_id}
                variant={
                  selected?.target_id === target.target_id
                    ? "contained"
                    : "outlined"
                }
                onClick={() => setSelected(target)}
                sx={{ justifyContent: "space-between" }}
              >
                {target.learner_display_name || "Learner"}
                <Chip size="small" label={statusLabel(target.state)} />
              </Button>
            )) ?? <Typography>Loading learner work…</Typography>}
            {targets.isError ? (
              <AcademyLoadError
                label="Learner work"
                retry={() => void targets.refetch()}
              />
            ) : null}
            {targets.data ? (
              <AcademyPager page={targets.data} onPage={setTargetOffset} />
            ) : null}
          </Stack>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          {selected ? (
            <SubmissionReviewPanel
              programId={programId}
              target={selected}
              onError={onError}
            />
          ) : (
            <EmptyState
              title="Select a learner"
              detail="Submission history and review controls appear here."
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SubmissionReviewPanel({
  programId,
  target,
  onError,
}: {
  programId: string;
  target: Schema["ClassAssignmentTarget"];
  onError: (message: string) => void;
}) {
  const [submissionOffset, setSubmissionOffset] = useState(0);
  const submissions = useClassSubmissions(
    target.target_id,
    target.enrollment_id,
    { offset: submissionOffset },
  );
  const review = useReviewClassSubmission(programId);
  const [body, setBody] = useState("");
  const latest = submissions.data?.items[0];
  const reviewRevision = latest?.review?.revision ?? 0;
  return (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6">
        {target.learner_display_name || "Learner"}
      </Typography>
      {submissions.isError ? (
        <AcademyLoadError
          label="Submissions"
          retry={() => void submissions.refetch()}
        />
      ) : !submissions.data ? (
        <Typography role="status">Loading submissions…</Typography>
      ) : !latest ? (
        <Typography color="text.secondary">No submission yet.</Typography>
      ) : (
        <>
          <Chip
            sx={{ alignSelf: "flex-start" }}
            label={statusLabel(latest.state)}
          />
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
            {latest.body || "File submission"}
          </Typography>
          {latest.attachments.map((file) => (
            <Button
              key={file.file_id}
              size="small"
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
              onClick={() =>
                void download(file.file_id, target.enrollment_id).catch(
                  (error) => onError(academyError(error)),
                )
              }
            >
              {file.filename}
            </Button>
          ))}
          <TextField
            label="Private feedback"
            multiline
            minRows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              disabled={!body.trim() || review.isPending}
              onClick={() =>
                void review
                  .mutateAsync({
                    submissionId: latest.submission_id,
                    body: {
                      body,
                      disposition: "reviewed",
                      expected_revision: reviewRevision,
                      idempotency_key: command(),
                    },
                  })
                  .then(() => setBody(""))
                  .catch((error) => onError(academyError(error)))
              }
            >
              Complete review
            </Button>
            <Button
              disabled={!body.trim() || review.isPending}
              onClick={() =>
                void review
                  .mutateAsync({
                    submissionId: latest.submission_id,
                    body: {
                      body,
                      disposition: "changes_requested",
                      expected_revision: reviewRevision,
                      idempotency_key: command(),
                    },
                  })
                  .then(() => setBody(""))
                  .catch((error) => onError(academyError(error)))
              }
            >
              Request changes
            </Button>
          </Stack>
        </>
      )}
      {submissions.data ? (
        <AcademyPager page={submissions.data} onPage={setSubmissionOffset} />
      ) : null}
    </Stack>
  );
}

function AcademyPager({
  page,
  onPage,
}: {
  page: { total: number; limit: number; offset: number };
  onPage: (offset: number) => void;
}) {
  if (page.total <= page.limit) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
      <Button
        size="small"
        disabled={page.offset <= 0}
        onClick={() => onPage(Math.max(0, page.offset - page.limit))}
      >
        Previous
      </Button>
      <Button
        size="small"
        disabled={page.offset + page.limit >= page.total}
        onClick={() => onPage(page.offset + page.limit)}
      >
        Next
      </Button>
    </Stack>
  );
}

function AcademyLoadError({
  label,
  retry,
}: {
  label: string;
  retry: () => void;
}) {
  return (
    <Alert
      severity="error"
      action={
        <Button size="small" onClick={retry}>
          Retry
        </Button>
      }
    >
      {label} could not be loaded.
    </Alert>
  );
}

function EmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box
      sx={{
        py: 4,
        px: 2,
        textAlign: "center",
        border: 1,
        borderStyle: "dashed",
        borderColor: "divider",
        borderRadius: 2,
        flex: 1,
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary">{detail}</Typography>
    </Box>
  );
}
